"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { LayoutDashboard, Ticket, Clock, BarChart3, Building, Search, Bell, MessageSquare } from "lucide-react";
import { Button } from "../button";
import { NavigationItem } from "./types";

interface ClientNavigationProps {
  className?: string;
}

export function ClientNavigation({ className }: ClientNavigationProps) {
  const pathname = usePathname() ?? "";

  // Reusable client navigation items (shared with mobile bottom drawer)
  // Keep IDs stable for customization and drawer state
  const navigation: NavigationItem[] = CLIENT_NAV_ITEMS;

  return (
    <nav className={cn("flex items-center space-x-1", className)}>
      {navigation.map((item) => {
        const IconComp = (item.icon || LayoutDashboard) as React.ComponentType<{
          className?: string;
        }>;
        const isCurrent =
          item.href === "/contacts/dashboard"
            ? pathname === item.href
            : pathname.startsWith(item.href);
        return (
          <div key={item.name} className="relative group">
            <Link
              href={item.href}
              className={cn(
                "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors",
                isCurrent
                  ? "bg-card-100 text-card-foreground"
                  : "text-gray-600 dark:text-gray-400 hover:text-card-foreground hover:bg-card-50"
              )}
            >
              <IconComp className="mr-2 h-4 w-4" />
              {item.name}
            </Link>

            {/* Dropdown for items with children */}
            {item.children && (
              <div className="absolute top-full left-0 mt-1 w-48 bg-card border border-gray-200 dark:border-gray-700 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-1">
                  {item.children.map((child) => (
                    <Link
                      key={child.id || child.name}
                      href={child.href}
                      className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-card-100 hover:text-card-foreground"
                    >
                      {child.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

export function ClientQuickActions() {
  return (
    <div className="flex items-center space-x-2">
      <Button size="sm" variant="outline" className="flex items-center space-x-1">
        <Search className="h-4 w-4" />
        <span>Search</span>
      </Button>

      <Button size="sm" className="flex items-center space-x-1">
        <Ticket className="h-4 w-4" />
        <span>Create Ticket</span>
      </Button>

      <Button size="sm" variant="outline" className="flex items-center space-x-1">
        <Bell className="h-4 w-4" />
        <span>Notifications</span>
      </Button>
    </div>
  );
}

// Exported for reuse by mobile bottom drawer
export const CLIENT_NAV_ITEMS: NavigationItem[] = [
  {
    id: "client-dashboard",
    name: "Dashboard",
    href: "/contacts/dashboard",
    icon: LayoutDashboard,
    visible: true,
    order: 0,
  },
  {
    id: "client-projects",
    name: "Projects",
    href: "/projects",
    icon: MessageSquare,
    visible: true,
    order: 0.5,
  },
  {
    id: "client-tickets",
    name: "Tickets",
    href: "/tickets/all-tickets",
    icon: Ticket,
    visible: true,
    order: 1,
    children: [
      { id: "client-tickets-my", name: "My Tickets", href: "/tickets/all-tickets" },
      { id: "client-tickets-create", name: "Create Ticket", href: "/tickets/create" },
    ],
  },
  {
    id: "client-time-entries",
    name: "Time Entries",
    href: "/time-tracking/entries",
    icon: Clock,
    visible: true,
    order: 2,
    children: [
      { id: "client-time-view", name: "View Time Entries", href: "/time-tracking/entries" },
      { id: "client-time-billing", name: "Billing Summary", href: "/contacts/reports/billing" },
    ],
  },
  {
    id: "client-reports",
    name: "Reports",
    href: "/contacts/reports",
    icon: BarChart3,
    visible: true,
    order: 3,
    children: [
      { id: "client-reports-tickets", name: "Ticket Reports", href: "/contacts/reports/tickets" },
      { id: "client-reports-time", name: "Time Reports", href: "/contacts/reports/time" },
      { id: "client-reports-billing", name: "Billing Reports", href: "/contacts/reports/billing" },
    ],
  },
];
